import type { ExtractedReviewItem } from '@/features/ai/InvoiceExtractorModal';
import type { ExtractedSpreadsheetItem } from './purchaseSpreadsheet';

export interface PurchaseReviewGroup {
  id: string;
  item: ExtractedReviewItem;
  members: ExtractedReviewItem[];
  quantity: number;
}

export interface PurchaseReviewModelGroup {
  id: string;
  label: string;
  kind: 'laptop' | 'other';
  variants: PurchaseReviewGroup[];
  quantity: number;
  total: number;
  missingSpecsCount: number;
}

// Physical-unit identifiers and source rows are deliberately kept on members,
// rather than used to split otherwise identical laptops into separate rows.
const laptopFields = [
  'brand', 'model', 'cpu', 'gpu', 'screen_size', 'touchscreen', 'resolution',
  'colour', 'keyboard_layout', 'operating_system', 'condition', 'grade',
  'battery_health', 'charger_included', 'charger_notes', 'accessories_included',
  'refurbishment_cost', 'other_cost', 'selling_price', 'minimum_price',
  'warranty_months', 'description', 'notes', 'is_public',
] as const;
const accessoryFields = [
  'accessory_id', 'accessory_name', 'accessory_sku', 'accessory_category',
  'accessory_brand', 'accessory_barcode', 'barcode', 'brand', 'selling_price', 'exact_sku_matched',
  'reorder_level', 'description', 'notes', 'is_public',
] as const;

export function isMissingLaptopSpec(value?: string | null): boolean {
  const spec = (value || '').trim();
  return !spec || /^(?:unknown|n\/?a|none|null|undefined|nil|not\s+(?:known|specified|provided|detected|available)|[-—?]+|0\s*(?:gb|mb)?)$/i.test(spec);
}

export function getReviewLaptopSpecs(item: ExtractedReviewItem) {
  return {
    ram: item.ram ?? item.draftItem?.ram ?? '',
    storage: item.storage ?? item.draftItem?.storage ?? '',
  };
}

export function isReviewLaptop(item: ExtractedReviewItem): boolean {
  return item.draftItem?.item_type === 'laptop' ||
    ((!item.draftItem || item.draftItem.item_type === 'unknown') && item.manualType === 'laptop');
}

function groupingKey(item: ExtractedReviewItem): string {
  const draft = item.draftItem;
  // Unclassified/manual lines remain individually reviewable.
  if (!draft || draft.item_type === 'unknown' || item.reviewSeparately) return JSON.stringify(['individual', item.id]);
  const laptop = draft.item_type === 'laptop';
  const specs = getReviewLaptopSpecs(item);
  return JSON.stringify([
    laptop ? 'laptop' : 'accessory', item.description.trim(), item.unit_cost,
    ...(laptop ? [specs.ram, specs.storage] : []),
    ...(laptop ? laptopFields : accessoryFields).map(field => draft[field] ?? null),
  ]);
}

export function groupPurchaseReviewItems(items: ExtractedReviewItem[]): PurchaseReviewGroup[] {
  const groups = new Map<string, PurchaseReviewGroup>();
  for (const item of items) {
    const key = groupingKey(item);
    const group = groups.get(key);
    if (group) {
      group.members.push(item);
      group.quantity += Number(item.quantity);
    } else {
      groups.set(key, { id: item.id, item, members: [item], quantity: Number(item.quantity) });
    }
  }
  return [...groups.values()];
}

// Model groups are display containers. Their variants retain their own hardware,
// prices and physical units; the model header never supplies a shared spec/cost.
export function groupPurchaseReviewModels(variants: PurchaseReviewGroup[]): PurchaseReviewModelGroup[] {
  const models = new Map<string, PurchaseReviewModelGroup>();
  const normalize = (value: string) => value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
  for (const variant of variants) {
    const item = variant.item;
    const draft = item.draftItem;
    const brand = (draft?.brand || '').trim().replace(/\s+/g, ' ');
    const model = (draft?.model || '').trim().replace(/\s+/g, ' ');
    // Keep unidentified and manually entered rows stable while they are edited.
    // In particular, do not combine all "Unknown Model" laptops into one model.
    const identified = draft?.item_type === 'laptop' && brand && model &&
      !isMissingLaptopSpec(brand) && !isMissingLaptopSpec(model) &&
      !/^(?:unknown\s+model|unspecified|laptop)$/i.test(model);
    const id = identified
      ? JSON.stringify(['model', normalize(brand), normalize(model)])
      : JSON.stringify(['individual', variant.id]);
    const specs = getReviewLaptopSpecs(item);
    const missing = isReviewLaptop(item) && (isMissingLaptopSpec(specs.ram) || isMissingLaptopSpec(specs.storage));
    const existing = models.get(id);
    if (existing) {
      existing.variants.push(variant);
      existing.quantity += variant.quantity;
      existing.total += variant.quantity * Number(item.unit_cost);
      existing.missingSpecsCount += missing ? 1 : 0;
    } else {
      models.set(id, {
        id,
        label: identified ? `${brand} ${model}` : item.description.trim() || 'New item — choose type and enter details',
        kind: isReviewLaptop(item) ? 'laptop' : 'other',
        variants: [variant],
        quantity: variant.quantity,
        total: variant.quantity * Number(item.unit_cost),
        missingSpecsCount: missing ? 1 : 0,
      });
    }
  }
  return [...models.values()];
}

export type ReviewGroupField = 'description' | 'quantity' | 'unit_cost' | 'ram' | 'storage';

export function updatePurchaseReviewGroup(
  items: ExtractedReviewItem[], memberIds: string[], field: ReviewGroupField, value: string | number,
): ExtractedReviewItem[] {
  const ids = new Set(memberIds);
  const members = items.filter(item => ids.has(item.id));
  if (!members.length) return items;
  let firstQuantity = Number(value);
  if (field === 'quantity' && members.length > 1) {
    const otherQuantity = members.slice(1).reduce((sum, item) => sum + Number(item.quantity), 0);
    if (!Number.isSafeInteger(firstQuantity) || firstQuantity < otherQuantity + 1) {
      throw new Error('To reduce this group, open View units and remove the specific rows you do not want.');
    }
    firstQuantity -= otherQuantity;
  }
  return items.map(item => {
    if (!ids.has(item.id) || (field === 'quantity' && item.id !== members[0].id)) return item;
    const nextValue = field === 'quantity' ? firstQuantity : value;
    return {
      ...item,
      [field]: nextValue,
      draftItem: item.draftItem ? {
        ...item.draftItem,
        [field === 'description' ? 'original_description' : field]: nextValue,
      } : undefined,
    };
  });
}

// Accessories are quantity-based stock. Consolidate identical lines at handoff
// too, so repeated new SKUs do not become several "new accessory" form entries.
// Laptops stay as physical units, with every original identifier intact.
export function consolidateReviewedAccessories(items: ExtractedSpreadsheetItem[]): ExtractedSpreadsheetItem[] {
  const result: ExtractedSpreadsheetItem[] = [];
  const accessories = new Map<string, ExtractedSpreadsheetItem>();
  for (const item of items) {
    if (item.item_type !== 'accessory' && item.item_type !== 'new_accessory') {
      result.push(item);
      continue;
    }
    const key = groupingKey({ id: item.id, description: item.original_description,
      quantity: item.quantity ?? 1, unit_cost: item.unit_cost, draftItem: item });
    const group = accessories.get(key);
    if (group) {
      group.quantity = (group.quantity ?? 1) + (item.quantity ?? 1);
      group.is_valid = group.is_valid && item.is_valid;
      group.warnings = [...new Set([...group.warnings, ...item.warnings])];
      group.duplicate_warning = [group.duplicate_warning, item.duplicate_warning].filter(Boolean).join('; ') || undefined;
    } else {
      const copy = { ...item, warnings: [...item.warnings] };
      accessories.set(key, copy);
      result.push(copy);
    }
  }
  return result;
}
